import { Injectable, computed, inject, signal } from '@angular/core';
import { RegsIndex, RegsWhere, Sector, Waters } from '../domain/regs-models';
import { RegsApiService } from './regs-api.service';

export type WhereProblem = 'noFix' | 'noServer';

@Injectable({ providedIn: 'root' })
export class RegsChoiceService {
  private api = inject(RegsApiService);

  readonly sector = signal<Sector>('recreational');
  readonly waters = signal<Waters>(Waters.nj);
  readonly here = signal<RegsWhere | null>(null);
  readonly hereFromBoat = signal<boolean>(false);
  readonly noPosition = signal<boolean>(false);
  readonly problem = signal<WhereProblem | null>(null);
  readonly locating = signal<boolean>(false);
  readonly chosenByHand = signal<boolean>(false);
  private readonly _idx = signal<RegsIndex | null>(null);
  private locatedAt: Date | null = null;

  get recentlyLocated(): boolean {
    return (
      this.here() != null &&
      this.locatedAt != null &&
      Date.now() - this.locatedAt.getTime() < 10 * 60 * 1000
    );
  }

  readonly hereWaters = computed<Waters | null>(() => {
    const h = this.here();
    if (!h) return null;
    const w = h.waters;
    if (w != null) return w;
    const p = h.place;
    const i = this._idx();
    return p != null && i != null && i.available(p) ? p : null;
  });

  readonly canShowRules = computed<boolean>(() => {
    return (
      this.chosenByHand() ||
      this.hereWaters() != null ||
      (this.here() == null && this.problem() == null)
    );
  });

  readonly showingElsewhere = computed<boolean>(() => {
    const w = this.hereWaters();
    return this.chosenByHand() && w != null && !w.equals(this.waters());
  });

  useIndex(idx: RegsIndex): void {
    this._idx.set(idx);
    const w = this.hereWaters();
    if (!this.chosenByHand() && w != null && !w.equals(this.waters())) {
      this.waters.set(w);
    }
  }

  pick(w: Waters): void {
    this.chosenByHand.set(true);
    this.waters.set(w);
  }

  useHere(): void {
    const w = this.hereWaters();
    if (!w) return;
    this.chosenByHand.set(false);
    this.waters.set(w);
  }

  set(opts: { sector?: Sector; waters?: Waters }): void {
    if (opts.sector) this.sector.set(opts.sector);
    if (opts.waters) this.waters.set(opts.waters);
  }

  async locate(quiet = false): Promise<void> {
    if (!quiet) this.locating.set(true);
    try {
      await this.performLocate();
    } finally {
      this.locating.set(false);
    }
  }

  private async performLocate(): Promise<void> {
    this.problem.set(null);
    let coords: { lat: number; lon: number } | null = null;

    if (typeof navigator !== 'undefined' && 'geolocation' in navigator) {
      try {
        coords = await new Promise<{ lat: number; lon: number } | null>((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
            () => resolve(null),
            { timeout: 7000, maximumAge: 60000 }
          );
        });
      } catch (_) {
        coords = null;
      }
    }

    if (!coords) {
      this.noPosition.set(true);
      this.problem.set('noFix');
      return;
    }

    this.noPosition.set(false);
    try {
      const res = await this.api.where(coords.lat, coords.lon);
      if (res) {
        this.here.set(res);
        this.hereFromBoat.set(false);
        this.locatedAt = new Date();
        const w = this.hereWaters();
        if (!this.chosenByHand() && w) {
          this.waters.set(w);
        }
      } else {
        this.problem.set('noServer');
      }
    } catch (_) {
      this.problem.set('noServer');
    }
  }
}
